import { Sparkles } from 'lucide-react'
import { AiFeedbackControl, ReportProblemButton, useAnalytics } from '../../../analytics'
import type { InsightCard } from '../../../data/ServerApiClient'
import { CFO_EVENTS, cfoInsightSurface, type CfoSurface } from './cfoTelemetry'
import { SEVERITY_LABEL, formatImpact, severityCardClass, trimStr } from './cfoFormat'
import type { UseCfoCopilotResult } from './useCfoCopilot'
import { CfoCardSkeletons } from './CfoPagePrimitives'

// CfoInsightCards — the calm "what changed / what to do" ranked cards for an
// interior page. Each card's "Explain" routes to the page-aware copilot (no
// logic re-derivation).

export type CfoInsightCardsProps = {
  cards: InsightCard[]
  loading: boolean
  error?: string
  copilot: UseCfoCopilotResult
  surface: CfoSurface
  workbookId: string | null
}

export function CfoInsightCards({ cards, loading, error, copilot, surface, workbookId }: CfoInsightCardsProps) {
  const analytics = useAnalytics()

  if (loading) {
    return <CfoCardSkeletons count={3} label="Ranking CFO insight cards…" />
  }
  if (error) {
    return <div className="cfo-shell-notice error">{error}</div>
  }
  if (!cards.length) {
    return <div className="cfo-shell-notice">No insight cards for this section yet.</div>
  }

  const explain = (card: InsightCard) => {
    analytics.track({
      event: CFO_EVENTS.insightExplain,
      surface,
      workbook_id: workbookId || null,
      props: { detector: card.detector, metric_id: card.impact.metric_id },
    })
    void copilot.sendMessage(
      `Explain this CFO insight: ${card.headline}. ${card.body} Explain the calculation, what it means operationally, why it matters now, and the next specific data to inspect.`,
      {
        selected_metric_id: trimStr(card.impact?.metric_id) || null,
        workspace_context: { interaction: 'insight_card_explain', insight_card: card },
      },
    )
    document.querySelector<HTMLTextAreaElement>('.cfo-copilot-input-box')?.focus()
  }

  return (
    <div className="cards" aria-live="polite">
      {cards.map((card) => {
        const sev = card.severity
        const dirDown = card.impact.direction === 'down'
        const feedbackSurface = cfoInsightSurface(surface)
        return (
          <article className={`card edge ${severityCardClass(sev)} rise`} key={card.id}>
            <div className="body">
              <span className="sev">{SEVERITY_LABEL[sev]}</span>
              <div className="grow">
                <h3>{card.headline}</h3>
                <p>{card.body}</p>
              </div>
              <div className="figc">
                <b className="num">{formatImpact(card)}</b>
                <span className="dir">{dirDown ? '▼' : card.impact.direction === 'up' ? '▲' : '◦'}</span>
                <span className="lbl">{card.impact.metric_id ? card.impact.metric_id.replace(/_/g, ' ') : ''}</span>
              </div>
            </div>
            <div className="actions">
              <button type="button" className="btn primary" onClick={() => explain(card)}>
                <Sparkles size={14} /> Explain
              </button>
              <div className="fb">
                <AiFeedbackControl aiTraceId={card.id} surface={feedbackSurface} workbookId={workbookId} />
                <ReportProblemButton
                  surface={feedbackSurface}
                  aiTraceId={card.id}
                  context={{ card_id: card.id, detector: card.detector }}
                />
              </div>
            </div>
          </article>
        )
      })}
    </div>
  )
}

export default CfoInsightCards
